import { useEffect, useMemo, useState } from 'react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EmptyState } from '@/components/EmptyState'
import { BarChart } from '@/components/charts/BarChart'
import { PieChart } from '@/components/charts/PieChart'
import { CategoryScatterChart } from '@/components/charts/CategoryScatterChart'
import { MODERN_CATEGORY_PALETTE, CHART_COLORS } from '@/components/charts/palette'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useAnalyzeStatements } from '@/features/analyzeStatements/queries'
import { useArAging } from '@/features/invoicing/queries'
import { useApAging } from '@/features/ap/queries'
import type {
  AnalyzeChartDisplay,
  AnalyzeScale,
  AnalyzeStatementRow,
  AnalyzeTransactionType,
} from '@/features/analyzeStatements/types'

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  return Number.isFinite(n) ? n : 0
}

function aggregate(
  rows: AnalyzeStatementRow[],
  type: AnalyzeTransactionType,
): Array<{ category: string; amount: number }> {
  const map = new Map<string, number>()
  for (const r of rows) {
    const key = r.category || 'Uncategorized'
    const amt = toNum(r.amount)
    let addend = 0
    if (type === 'income' && amt > 0) addend = amt
    else if (type === 'expenses' && amt < 0) addend = Math.abs(amt)
    else if (type === 'both') addend = Math.abs(amt)
    if (addend === 0) continue
    map.set(key, (map.get(key) ?? 0) + addend)
  }
  return Array.from(map.entries())
    .map(([category, amount]) => ({ category, amount }))
    .sort((a, b) => b.amount - a.amount)
}

function todayIso(): string {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}

export function AnalyzeStatementsPage() {
  const [fromDate, setFromDate] = useState('2000-01-01')
  const [toDate, setToDate] = useState<string>(todayIso())
  const [pendingFrom, setPendingFrom] = useState(fromDate)
  const [pendingTo, setPendingTo] = useState(toDate)
  const [transactionType, setTransactionType] = useState<AnalyzeTransactionType>('both')
  const [chartType, setChartType] = useState<AnalyzeChartDisplay>('bar')
  const [scale, setScale] = useState<AnalyzeScale>('logarithmic')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [selection, setSelection] = useState<{ label: string; value: number } | null>(null)

  const { data, isLoading, isError, error } = useAnalyzeStatements(fromDate, toDate, true)
  // Aging reports default to "as of today"; surface alongside the spending
  // analysis so the user gets a single Analyse view that covers both
  // historical spend AND the current AR/AP picture.
  const arQ = useArAging()
  const apQ = useApAging()

  const aggregated = useMemo(
    () => aggregate(data ?? [], transactionType),
    [data, transactionType],
  )

  const arAging = arQ.data?.report
  const apAging = apQ.data?.report

  const arBucketLabels = useMemo(
    () => (arAging ? arAging.buckets.map((b) => b.label) : []),
    [arAging],
  )
  const arBucketValues = useMemo(
    () => (arAging ? arAging.buckets.map((b) => Number(arAging.totals[b.key]) || 0) : []),
    [arAging],
  )
  const apBucketLabels = useMemo(
    () => (apAging ? apAging.buckets.map((b) => b.label) : []),
    [apAging],
  )
  const apBucketValues = useMemo(
    () => (apAging ? apAging.buckets.map((b) => Number(apAging.totals[b.key]) || 0) : []),
    [apAging],
  )

  const topCustomers = useMemo(() => {
    if (!arAging) return [] as Array<{ label: string; amount: number }>
    return [...arAging.customers]
      .sort((a, b) => Number(b.total) - Number(a.total))
      .slice(0, 10)
      .map((c) => ({ label: c.customer_display_name || `#${c.customer_id}`, amount: Number(c.total) || 0 }))
  }, [arAging])

  const topVendors = useMemo(() => {
    if (!apAging) return [] as Array<{ label: string; amount: number }>
    return [...apAging.vendors]
      .sort((a, b) => Number(b.total) - Number(a.total))
      .slice(0, 10)
      .map((v) => ({ label: v.vendor_display_name || `#${v.vendor_id}`, amount: Number(v.total) || 0 }))
  }, [apAging])

  const arHasData = arAging != null && Number(arAging.totals.total) > 0
  const apHasData = apAging != null && Number(apAging.totals.total) > 0

  // Clear click-selection whenever chart type, scale, or aggregation changes.
  useEffect(() => {
    setSelection(null)
  }, [chartType, scale, transactionType, fromDate, toDate])

  const applyFilter = () => {
    setValidationError(null)
    if (!pendingFrom || !pendingTo) {
      setValidationError("Please select both 'From' and 'To' dates.")
      return
    }
    if (new Date(pendingFrom) > new Date(pendingTo)) {
      setValidationError("'From' date cannot be after 'To' date.")
      return
    }
    setFromDate(pendingFrom)
    setToDate(pendingTo)
  }

  const hasData = aggregated.length > 0
  const labels = aggregated.map((a) => a.category)
  const values = aggregated.map((a) => a.amount)
  const perBarColors = labels.map((_, i) => MODERN_CATEGORY_PALETTE[i % MODERN_CATEGORY_PALETTE.length])

  return (
    <Page
      title="Analyze Statements"
      description="Aggregated spending analysis by category across your chosen period."
      width="wide"
    >
      <Card className="p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="an-from">From</Label>
            <Input
              id="an-from"
              type="date"
              value={pendingFrom}
              onChange={(e) => setPendingFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="an-to">To</Label>
            <Input
              id="an-to"
              type="date"
              value={pendingTo}
              onChange={(e) => setPendingTo(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="an-data">Data</Label>
            <Select value="spendingByCategory" onValueChange={() => undefined}>
              <SelectTrigger id="an-data">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spendingByCategory">Spending by Category</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="an-type">Transaction Type</Label>
            <Select value={transactionType} onValueChange={(v) => setTransactionType(v as AnalyzeTransactionType)}>
              <SelectTrigger id="an-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expenses">Expenses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-2">
            <Label>Chart Type</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={chartType === 'bar' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setChartType('bar')}
              >
                Vertical Bar
              </Button>
              <Button
                type="button"
                variant={chartType === 'scatter' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setChartType('scatter')}
              >
                Scatter
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label>Scale</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={scale === 'linear' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setScale('linear')}
              >
                Linear
              </Button>
              <Button
                type="button"
                variant={scale === 'logarithmic' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setScale('logarithmic')}
              >
                Logarithmic
              </Button>
            </div>
          </div>
          <div className="flex items-end">
            <Button type="button" variant="primary" onClick={applyFilter}>
              Apply Filter &amp; Plot
            </Button>
          </div>
        </div>
      </Card>

      {validationError ? (
        <Alert variant="destructive">
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Could not load or plot data: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      ) : null}

      {selection ? (
        <p className="text-sm text-muted-foreground">
          Selected: <span className="font-semibold text-foreground">{selection.label}</span> -
          Amount:{' '}
          <span className="font-semibold text-foreground">{formatCurrencyPrecise(selection.value)}</span>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">Click on a chart segment for details.</p>
      )}

      {isLoading ? (
        <Card className="p-6">
          <EmptyState title="Loading…" description="Fetching statement data." />
        </Card>
      ) : !hasData ? (
        <Card className="p-6">
          <EmptyState
            title="No data"
            description="No spending data available for the selected criteria."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Spending by category</h3>
            {chartType === 'bar' ? (
              <BarChart
                labels={labels}
                series={[{ label: 'Spending', values, perBarColors }]}
                logarithmic={scale === 'logarithmic'}
                hideDataLabels
                showLegend={false}
                barThickness={32}
                formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
                onBarClick={(label, value) => setSelection({ label, value })}
                height={360}
                ariaLabel="Spending bar chart"
              />
            ) : (
              <CategoryScatterChart
                labels={labels}
                values={values}
                colors={MODERN_CATEGORY_PALETTE}
                logarithmic={scale === 'logarithmic'}
                formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
                onPointClick={(label, value) => setSelection({ label, value })}
                height={360}
                ariaLabel="Spending scatter chart"
              />
            )}
          </Card>
          <Card className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Spending distribution</h3>
            <PieChart
              labels={labels}
              values={values}
              colors={MODERN_CATEGORY_PALETTE}
              formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
              onSliceClick={(label, value) => setSelection({ label, value })}
              height={360}
              ariaLabel="Spending pie chart"
            />
          </Card>
        </div>
      )}

      {/* AR / AP visualisations. Each section shows the bucket breakdown + the
          top 10 counterparties so the user can compare receivables and
          payables side-by-side without leaving Analyse Statements. Hides itself
          when there is no AR/AP data so a fresh tenant doesn't see five empty
          cards stacked under spending. */}
      {(arQ.isLoading || apQ.isLoading || arHasData || apHasData) ? (
        <div className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">Receivables &amp; payables</h2>
          <p className="text-sm text-muted-foreground">
            As of today &middot; Aging buckets aggregate every open invoice / bill by
            how many days it is past its due date.
          </p>
        </div>
      ) : null}

      {arHasData ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              AR aging &middot; outstanding {formatCurrencyPrecise(Number(arAging?.totals.total) || 0)}
            </h3>
            <BarChart
              labels={arBucketLabels}
              series={[{
                label: 'Receivable',
                values: arBucketValues,
                perBarColors: [
                  CHART_COLORS.success,
                  CHART_COLORS.secondary,
                  CHART_COLORS.warning,
                  '#F37E22',
                  CHART_COLORS.danger,
                ],
              }]}
              hideDataLabels
              showLegend={false}
              barThickness={36}
              formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
              height={320}
              ariaLabel="AR aging bar chart"
            />
          </Card>
          <Card className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Top customers by AR</h3>
            {topCustomers.length > 0 ? (
              <BarChart
                labels={topCustomers.map((c) => c.label)}
                series={[{
                  label: 'Outstanding',
                  values: topCustomers.map((c) => c.amount),
                  color: CHART_COLORS.primary,
                }]}
                hideDataLabels
                showLegend={false}
                barThickness={22}
                formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
                height={320}
                ariaLabel="Top customers by AR"
              />
            ) : (
              <EmptyState title="No AR" description="No customers have an open balance." />
            )}
          </Card>
        </div>
      ) : null}

      {apHasData ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              AP aging &middot; outstanding {formatCurrencyPrecise(Number(apAging?.totals.total) || 0)}
            </h3>
            <BarChart
              labels={apBucketLabels}
              series={[{
                label: 'Payable',
                values: apBucketValues,
                perBarColors: [
                  CHART_COLORS.success,
                  CHART_COLORS.secondary,
                  CHART_COLORS.warning,
                  '#F37E22',
                  CHART_COLORS.danger,
                ],
              }]}
              hideDataLabels
              showLegend={false}
              barThickness={36}
              formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
              height={320}
              ariaLabel="AP aging bar chart"
            />
          </Card>
          <Card className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Top vendors by AP</h3>
            {topVendors.length > 0 ? (
              <BarChart
                labels={topVendors.map((v) => v.label)}
                series={[{
                  label: 'Outstanding',
                  values: topVendors.map((v) => v.amount),
                  color: CHART_COLORS.danger,
                }]}
                hideDataLabels
                showLegend={false}
                barThickness={22}
                formatTooltip={(label, value) => `${label}: ${formatCurrencyPrecise(value)}`}
                height={320}
                ariaLabel="Top vendors by AP"
              />
            ) : (
              <EmptyState title="No AP" description="No vendors have an open balance." />
            )}
          </Card>
        </div>
      ) : null}
    </Page>
  )
}
